<template>
    <div class="ppv2-min-h-[160px] ppv2-border ppv2-border-black ppv2-border-opacity-50 ppv2-rounded-[12px]">
        <h2
            class="poppinsRegular ppv2-text-black"
            :style="{
                fontSize: normalizeFontSize(15),
                paddingLeft: normalizeSize(27, true),
                paddingTop: normalizeSizeVertical(18, true),
            }"
        >
            {{ $t('experience.step5.specificMode.title') }}
        </h2>
        <transition name="fade">
            <div
                class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center ppv2-w-full"
                :style="{ gap: normalizeSize(14, true) }"
            >
                <table
                    v-if="configuredTarifs.length > 0"
                    class="ppv2-w-full text-left ppv2-text-black"
                    :style="{ fontSize: '11px' }"
                >
                    <thead>
                        <tr class="py-1 space-x-2">
                            <th class="poppinsMedium" :style="{ paddingLeft: normalizeSize(27, true) }">
                                {{ $t('experience.step5.specificMode.dates') }}
                            </th>
                            <th class="poppinsMedium">{{ $t('configuration.cards.schedule.title') }}</th>
                            <th class="poppinsMedium">{{ $t('experience.step5.specificMode.tarif') }}</th>
                            <th class="poppinsMedium">{{ $t('experience.timeSpecific.allowMixing') }}</th>
                            <th class="poppinsMedium text-right" :style="{ paddingRight: normalizeSize(27, true) }"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(tarif, index) in configuredTarifs"
                            :key="index"
                            class="evenItem poppinsLight ppv2-text-black ppv2-py-1"
                            :style="{ fontSize: '11px' }"
                        >
                            <td
                                class="py-1"
                                :style="{ paddingLeft: normalizeSize(27, true), maxWidth: normalizeSize(250, true) }"
                            >
                                <div class="poppinsMedium">{{ tarif.label }}</div>
                                <small class="italic">{{ formatDays(tarif) }}</small>
                            </td>
                            <td class="py-1">{{ tarif.hours }}</td>
                            <td class="py-1">{{ format_price(tarif.price) }}</td>
                            <td class="py-1">{{ format_auth(tarif.authorize_mixing) }}</td>
                            <td class="text-right space-x-2 py-1" :style="{ paddingRight: normalizeSize(27, true) }">
                                <SquarePen class="ppv2-cursor-pointer ppv2-ml-2" :size="15" @click="editTarif(tarif, index)" />
                                <CopyPlus
                                    class="ppv2-cursor-pointer ppv2-ml-2"
                                    :size="15"
                                    @click="$emit('duplicateTarif', tarif, index)"
                                />
                                <Trash2 class="ppv2-cursor-pointer" :size="15" @click="$emit('deleteTarif', index)" />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <p v-else class="!ppv2-mb-0 ppv2-text-black" style="font-size: 12px">
                    {{ $t('experience.step5.specificMode.emptyTarif') }}
                </p>
                <div class="text-center">
                    <BaseButton
                        id="button"
                        type="button"
                        variant="secondary"
                        @click="showTimeSpecific = true"
                        :customFontSize="12"
                        :style="{ marginBottom: normalizeSizeVertical(34, true) }"
                    >
                        <span>{{ $t('experience.step5.specificMode.addTarif') }}</span>
                    </BaseButton>
                </div>
            </div>
        </transition>

        <TimeSpecific
            v-model="showTimeSpecific"
            mode="tarif"
            :titlePopup="$t('experience.step5.specificMode.titlePopup')"
            :defaultTitle="$t('experience.step5.specificMode.popupTitleSection')"
            :editing-data="editingTarif"
            :editing-index="editingIndex"
            :subCategoryId="subCategoryId"
            :establishmentId="establishmentId"
            :prestation="prestation"
            :horaires_specifiques="configuredTarifs"
            @save="handleSaveTarif"
            @cancel="handleCancel"
        />
    </div>
</template>

<script>
import TimeSpecific from './TimeSpecific.vue'
import normalizeSize from '../../../../utils/normalizeSize'
import { CopyPlus, SquarePen, Trash2 } from 'lucide-vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import normalizeFontSize from '../../../../utils/normalizeFontSize'

export default {
    name: 'ListTarifSpecifique',
    components: { TimeSpecific, BaseButton, SquarePen, Trash2, CopyPlus },
    props: {
        configuredTarifs: {
            type: Array,
            required: true,
        },
        subCategoryId: {
            type: [Number, String],
            default: null,
        },
        establishmentId: {
            type: [Number, String],
            default: null,
        },
        prestation: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            showTimeSpecific: false,
            editingTarif: null,
            editingIndex: -1,
        }
    },
    methods: {
        normalizeFontSize,
        normalizeSizeVertical,
        normalizeSize,
        handleSaveTarif(tarifData) {
            const newTarif = {
                ...tarifData,
                label: !tarifData.dateEnd
                    ? `${this.$t('common.from')} ${this.formatDate(tarifData.dateStart)}`
                    : `${this.$t('common.from')} ${this.formatDate(tarifData.dateStart)} ${this.$t(
                          'common.to_date'
                      )} ${this.formatDate(tarifData.dateEnd)}`,
                label_days: this.formatDays(tarifData),
                hours: `${tarifData.timeStart} - ${tarifData.timeEnd}`,
                price: tarifData.price,
            }

            if (this.editingIndex >= 0) {
                this.$emit('updateTarif', this.editingIndex, newTarif)
            } else {
                this.$emit('addTarif', newTarif)
            }

            this.resetEditing()
        },
        editTarif(tarif, index) {
            this.editingTarif = {
                ...tarif,
                selectedDays: tarif.selectedDays,
            }
            this.editingIndex = index
            this.showTimeSpecific = true
        },
        handleCancel() {
            this.resetEditing()
        },
        resetEditing() {
            this.editingTarif = null
            this.editingIndex = -1
            this.showTimeSpecific = false
        },
        formatDate(dateValue) {
            if (!dateValue) return ''

            if (typeof dateValue === 'string') {
                const [year, month, day] = dateValue.split('-')
                const dateObj = new Date(year, month - 1, day)
                const options = { day: 'numeric', month: 'long', year: 'numeric' }
                return dateObj.toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', options)
            }

            if (dateValue instanceof Date) {
                const options = { day: 'numeric', month: 'long', year: 'numeric' }
                return dateValue.toLocaleDateString(this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US', options)
            }

            return ''
        },
        formatDays(horaire) {
            const raw = horaire.selectedDays
            if (!Array.isArray(raw) || raw.length === 0) return ''

            // Flatten: selectedDays may contain comma-joined strings like "Monday, Tuesday, ..."
            const daysArray = raw
                .flatMap((d) => (typeof d === 'string' ? d.split(',').map((s) => s.trim()) : [d]))
                .filter(Boolean)
            if (daysArray.length === 0) return ''

            const allDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
            const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']
            const weekendDays = ['Saturday', 'Sunday']

            const selected = new Set(daysArray)

            if (daysArray.length === 7) return this.$t('common.allDays')

            if (weekDays.every((d) => selected.has(d)) && weekendDays.every((d) => !selected.has(d))) {
                return this.$t('common.allDaysNoWeekend')
            }

            if (weekendDays.every((d) => selected.has(d)) && weekDays.every((d) => !selected.has(d))) {
                return this.$t('common.weekendDays')
            }

            const missingDays = allDays.filter((d) => !selected.has(d))
            if (missingDays.length > 0 && missingDays.length <= 2 && daysArray.length >= 5) {
                const exceptList = missingDays.map((d) => this.$t(`resource_planning.week_days.${d.toLowerCase()}`))
                if (exceptList.length === 1) return `${this.$t('common.allDaysExpect')} ${exceptList[0]}`
                return `${this.$t('common.allDaysExpect')} ${exceptList.join(` ${this.$t('common.and')} `)}`
            }

            const dayNames = daysArray.map((d) => this.$t(`resource_planning.week_days.${d.toLowerCase()}`))

            if (dayNames.length === 1) {
                if (horaire.dateStart && horaire.dateEnd && horaire.dateStart === horaire.dateEnd) {
                    return dayNames[0]
                }
                return `${this.$t('common.allSpecificDay')} ${dayNames[0]}`
            }

            return `${this.$t('common.allSpecificDay')} ${dayNames.slice(0, -1).join(', ')} ${this.$t(
                'common.and'
            )} ${dayNames.slice(-1)}`
        },
        format_price(price) {
            return parseFloat(price).toFixed(2)
        },
        format_auth(autorized_mix) {
            return autorized_mix == 1 ? this.$t('common.yes') : this.$t('common.no')
        },
    },
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
